<template>
  <page>
    <h1>Mjesečni izvještaj CoreEvent prihoda</h1>
    <h2>Period od {{ formatDate(from) }} do {{ formatDate(to) }}</h2>

    <!-- Fixed Fee Events -->
    <h3>Fiksne Naknade</h3>
    <table>
      <thead>
        <tr>
          <th>Događaj</th>
          <th>Organizator</th>
          <th>Cijena Naknade (€)</th>
          <th>Količina</th>
          <th>Ukupno Naknada (€)</th>
          <th>PDV na Naknadu (€)</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="item in reportData" :key="item.eventId">
          <td>{{ item.eventTitle }}</td>
          <td>{{ item.organization }}</td>
          <td>
            {{
              formatCurrency(
                item.totalCoreEventEarningsWithoutVat / item.coreEventFeeTicketCount || 0,
              )
            }}
          </td>
          <td>{{ item.ticketCount }}</td>
          <td>{{ formatCurrency(item.totalCoreEventEarningsWithoutVat) }}</td>
          <td>{{ formatCurrency(item.totalCoreEventEarningsWithoutVat * 0.25) }}</td>
        </tr>
      </tbody>
    </table>

    <!-- Other Events -->
    <h3 v-if="!fixedFeeOnly">Postoci od ulaznica</h3>
    <table v-if="!fixedFeeOnly">
      <thead>
        <tr>
          <th>Događaj</th>
          <th>Organizator</th>
          <th>Količina</th>
          <th>Ukupno Prodano (€)</th>
          <th>CoreEvent Naknada (€)</th>
          <th>CoreEvent Naknada s PDV-om (€)</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="summary in purchaseSummaries" :key="summary.event.id">
          <td>{{ summary.event.title }}</td>
          <td>{{ summary.event.organization.legalName }}</td>
          <td>{{ summary.totalSoldCount }}</td>
          <td>{{ formatCurrency(summary.totalSoldAmount) }}</td>
          <td>{{ formatCurrency(summary.totalCoreEventFee) }}</td>
          <td>{{ formatCurrency(summary.totalCoreEventFeeWithVAT) }}</td>
        </tr>
      </tbody>
    </table>

    <!-- Totals -->
    <div class="totals">
      <h3>
        <span>Ukupno Fiksne Naknade:</span>
        <span>{{ formatCurrency(totalCoreEventFixedFeeEarnings) }} €</span>
      </h3>
      <h3 v-if="!fixedFeeOnly">
        <span>Ukupno Ostale Naknade:</span>
        <span>{{ formatCurrency(totalCoreEventEarnings - totalCoreEventFixedFeeEarnings) }} €</span>
      </h3>
      <h3 v-if="!fixedFeeOnly">
        <span>Ukupno Naknada:</span>
        <span>{{ formatCurrency(totalCoreEventEarnings) }} €</span>
      </h3>
      <h3>
        <span>Ukupno PDV na Naknadu:</span>
        <span>{{ formatCurrency(totalCoreEventEarnings * 0.25) }} €</span>
      </h3>
      <hr />
      <h3>
        <span>Ukupno Naknada s PDV-om:</span>
        <span>{{ formatCurrency(totalCoreEventEarnings * 1.25) }} €</span>
      </h3>
    </div>
  </page>
</template>

<script>
import moment from 'moment'
import gql from 'graphql-tag'

export default {
  props: {
    year: String,
    month: String,
    fixedFeeOnly: String,
  },
  data() {
    return {
      from: null,
      to: null,
      reportData: [],
      purchaseSummaries: [],
      totalCoreEventFixedFeeEarnings: 0,
      totalCoreEventEarnings: 0,
      refundInfo: null,
    }
  },
  methods: {
    formatDate(date) {
      return moment(date).format('DD. MM. YYYY HH:mm')
    },
    formatCurrency(value) {
      return parseFloat(value).toFixed(2)
    },
  },
  apollo: {
    getMonthlyReport: {
      query: gql`
        query GetMonthlyReport($year: Int!, $month: Int!, $fixedFeeOnly: Boolean) {
          getCoreEventMonthlyReport(year: $year, month: $month, fixedFeeOnly: $fixedFeeOnly)
        }
      `,
      variables() {
        return {
          year: this.year ? parseInt(this.year) : new Date().getFullYear(),
          month: this.month ? parseInt(this.month) : new Date().getMonth() + 1,
          fixedFeeOnly: !!this.fixedFeeOnly,
        }
      },
      result({ data }) {
        if (data && data.getCoreEventMonthlyReport) {
          const report = data.getCoreEventMonthlyReport
          this.from = report.from
          this.to = report.to
          this.reportData = report.reportData
            .filter(r => !!r.totalCoreEventEarningsWithoutVat)
            .sort((a, b) => b.totalCoreEventEarningsWithoutVat - a.totalCoreEventEarningsWithoutVat)
          this.purchaseSummaries = (report.purchaseSummaries || [])
            .filter(r => !!r.totalCoreEventFeeWithVAT)
            .sort((a, b) => b.totalCoreEventFeeWithVAT - a.totalCoreEventFeeWithVAT)
          this.totalCoreEventFixedFeeEarnings = report.totalCoreEventFixedFeeEarnings
          this.totalCoreEventEarnings = report.totalCoreEventEarnings
          this.refundInfo = report.refundInfo
        }
      },
    },
  },
}
</script>

<style scoped>
page {
  background: white;
  max-width: 1200px;
  margin: auto;
  display: block;
}

@media print {
  body,
  page[size='A4'] {
    margin: 0;
    box-shadow: 0;
  }
}

.container {
  padding: 60px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
}

.company-info {
  align-self: end;
}

.nowrap {
  white-space: nowrap;
}

.totals {
  align-self: end;
}

.totals > h3 > span:nth-of-type(2) {
  float: right;
  margin-left: 10px;
}

/* Center tables for demo */
table {
}

/* Default Table Style */
table {
  color: #333;
  background: white;
  border: 1px solid grey;
  font-size: 12pt;
  border-collapse: collapse;
}

table thead th,
table tfoot th {
  color: #777;
  background: rgba(0, 0, 0, 0.1);
}

table caption {
  padding: 0.5em;
}

table th,
table td {
  padding: 0.5em;
  border: 1px solid lightgrey;
}

/* Zebra Table Style */
[data-table-theme*='zebra'] tbody tr:nth-of-type(odd) {
  background: rgba(0, 0, 0, 0.05);
}

[data-table-theme*='zebra'][data-table-theme*='dark'] tbody tr:nth-of-type(odd) {
  background: rgba(255, 255, 255, 0.05);
}

/* Dark Style */
[data-table-theme*='dark'] {
  color: #ddd;
  background: #333;
  font-size: 12pt;
  border-collapse: collapse;
}

[data-table-theme*='dark'] thead th,
[data-table-theme*='dark'] tfoot th {
  color: #aaa;
  background: rgba(0255, 255, 255, 0.15);
}

[data-table-theme*='dark'] caption {
  padding: 0.5em;
}

[data-table-theme*='dark'] th,
[data-table-theme*='dark'] td {
  padding: 0.5em;
  border: 1px solid grey;
}
</style>
